/**
 * Copyright(c) 2024 Magrabi
 *
 * All rights reserved.
 *
 * @module mReserve Client-Side API
 */

import { MReserveService, MReserveStore, MReserveTimeSlot, MReserveBooking, CreateBookingRequest, RescheduleBookingRequest } from "./types";

/**
 * Get all services
 */
export const getServices = async (locale?: string): Promise<MReserveService[]> => {
  const url = new URL("/api/mreserve/services", window.location.origin);
  if (locale) url.searchParams.append("locale", locale);

  const response = await fetch(url.toString());
  if (!response.ok) throw new Error("Failed to fetch services");
  const result = await response.json();
  return result.data || [];
};

/**
 * Get single service
 */
export const getService = async (serviceId: number, locale?: string): Promise<MReserveService> => {
  const url = new URL(`/api/mreserve/services`, window.location.origin);
  url.searchParams.append("id", serviceId.toString());
  if (locale) url.searchParams.append("locale", locale);

  const response = await fetch(url.toString());
  if (!response.ok) throw new Error("Failed to fetch service");
  const result = await response.json();
  return Array.isArray(result.data) ? result.data[0] : result.data;
};

/**
 * Get stores by service
 */
export const getStores = async (serviceId: number, params?: { city?: string; name?: string }, locale?: string): Promise<MReserveStore[]> => {
  const url = new URL("/api/mreserve/stores", window.location.origin);
  url.searchParams.append("serviceId", serviceId.toString());
  if (params?.city) url.searchParams.append("city", params.city);
  if (params?.name) url.searchParams.append("name", params.name);
  if (locale) url.searchParams.append("locale", locale);

  const response = await fetch(url.toString());
  if (!response.ok) throw new Error("Failed to fetch stores");
  return response.json();
};

/**
 * Get time slots
 */
export const getTimeSlots = async (serviceId: number, storeId: number, date: string, locale?: string): Promise<MReserveTimeSlot[]> => {
  const url = new URL("/api/mreserve/slots", window.location.origin);
  url.searchParams.append("serviceId", serviceId.toString());
  url.searchParams.append("storeId", storeId.toString());
  url.searchParams.append("date", date);
  if (locale) url.searchParams.append("locale", locale);

  const response = await fetch(url.toString());
  if (!response.ok) throw new Error("Failed to fetch time slots");
  const data = await response.json();
  return data.data?.slots || [];
};

/**
 * Create booking
 */
export const createBooking = async (bookingData: CreateBookingRequest, locale?: string): Promise<MReserveBooking> => {
  const url = new URL("/api/mreserve/bookings", window.location.origin);
  if (locale) url.searchParams.append("locale", locale);

  const response = await fetch(url.toString(), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(bookingData),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: "Failed to create booking" }));
    throw new Error(error.message || "Failed to create booking");
  }

  const result = await response.json();
  return result.data;
};

/**
 * Get booking by ID
 */
export const getBooking = async (bookingId: string, locale?: string): Promise<MReserveBooking> => {
  const url = new URL(`/api/mreserve/bookings/${bookingId}`, window.location.origin);
  if (locale) url.searchParams.append("locale", locale);

  const response = await fetch(url.toString());
  if (!response.ok) throw new Error("Failed to fetch booking");
  const result = await response.json();
  return result.data;
};

/**
 * Cancel booking
 */
export const cancelBooking = async (bookingId: string, cancelToken: string, locale?: string): Promise<MReserveBooking> => {
  const url = new URL(`/api/mreserve/bookings/${bookingId}/cancel`, window.location.origin);
  if (locale) url.searchParams.append("locale", locale);

  const response = await fetch(url.toString(), {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ cancelToken }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: "Failed to cancel booking" }));
    throw new Error(error.message || "Failed to cancel booking");
  }

  const result = await response.json();
  return result.data;
};

/**
 * Reschedule booking
 */
export const rescheduleBooking = async (
  bookingId: string,
  rescheduleToken: string,
  bookingData: RescheduleBookingRequest,
  locale?: string
): Promise<MReserveBooking> => {
  const url = new URL(`/api/mreserve/bookings/${bookingId}/reschedule`, window.location.origin);
  if (locale) url.searchParams.append("locale", locale);

  const response = await fetch(url.toString(), {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(bookingData),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: "Failed to reschedule booking" }));
    throw new Error(error.message || "Failed to reschedule booking");
  }

  const result = await response.json();
  return result.data;
};
